import {
  withCompInfo,
  withStateRefsValues,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import type {
  ImageXProps,
  ImageXDefinition,
} from '@wix/thunderbolt-components-native';
import type {
  IImageXProps,
  IImageXStateValues,
  ObjectFit,
  ResponsiveImageInfo,
} from '../ImageX.types';
import {
  getResponsiveImageObjectFit,
  getResponsiveImageSources,
  hasValidUnits,
} from './ImageX.utils';
import type { Encoding } from '@wix/image-kit';

export const props = withCompInfo<
  ImageXProps,
  ImageXDefinition,
  IImageXProps
>()(
  [
    'fullNameCompType',
    'language',
    'compId',
    'mainPageId',
    'trackClicksAnalytics',
  ],
  ({ ...compPropsRest }, carmiProps: IImageXProps) => {
    const {
      imageInfo: { alignType, imageData, hasAnimation },
      responsiveImagesInfo,
      shouldUseResponsiveImages,
      hasScrollEffects,
      shouldUseAVIFEncoding,
    } = carmiProps;

    const { width, height } = imageData.crop ? imageData.crop : imageData;
    const aspectRatio = height / width;

    const responsiveImagesOptions = {
      hasAnimation,
      ...(shouldUseAVIFEncoding ? { encoding: 'AVIF' as Encoding } : {}),
    };

    const shouldSkipResponsiveImages =
      !responsiveImagesInfo ||
      !shouldUseResponsiveImages ||
      responsiveImagesInfo?.some(
        (data: ResponsiveImageInfo) =>
          !hasValidUnits(data.layout) || hasScrollEffects,
      );

    const sources = !shouldSkipResponsiveImages
      ? (getResponsiveImageSources(
          responsiveImagesInfo,
          imageData,
          alignType,
          responsiveImagesOptions,
        ) as IImageXProps['sources'])
      : [];

    const objectFit: ObjectFit =
      !shouldSkipResponsiveImages && sources?.length
        ? getResponsiveImageObjectFit(
            sources,
            responsiveImagesInfo,
            imageData,
            alignType,
          )
        : 'cover';

    const sourcesWithSrcset = sources.filter(s => !!s?.srcset);
    const filteredSources = sourcesWithSrcset
      .filter(
        (s, index: number) =>
          index === 0 || s?.srcset !== sourcesWithSrcset[index - 1]?.srcset,
      )
      .reverse();

    return {
      ...compPropsRest,
      ...carmiProps,
      sources: filteredSources,
      responsiveImagesInfo,
      objectFit,
      aspectRatio,
      imageInfo: {
        ...carmiProps.imageInfo,
        ...(shouldUseAVIFEncoding ? { encoding: 'AVIF' as Encoding } : {}),
      },
    };
  },
);

const stateRefs = withStateRefsValues<keyof IImageXStateValues>(['reportBi']);

export default createComponentMapperModel({
  props,
  stateRefs,
});
